import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  CalendarMonth,
  Refresh,
  Clear,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

const DateRangeSelector = ({ onFetchThreads, isLoading = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [dateRange, setDateRange] = useState([null, null]);
  const [isOpen, setIsOpen] = useState(false);
  
  const handleFetch = () => {
    if (dateRange[0] && dateRange[1]) {
      // Convert dates to ISO format with timezone
      const startDate = dateRange[0].startOf('day').toDate();
      const endDate = dateRange[1].endOf('day').toDate();
      
      onFetchThreads(startDate.toISOString(), endDate.toISOString());
    }
  };

  const handleClear = () => {
    setDateRange([null, null]);
  };

  const isValidRange = dateRange[0] && dateRange[1];

  return (
    <Card 
      elevation={2} 
      sx={{ 
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CalendarMonth sx={{ fontSize: 24, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('dateRangeSelector.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('dateRangeSelector.detailedDescription')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setIsOpen(!isOpen)}
            color="primary"
            size="large"
          >
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={isOpen}>
          <Box sx={{ position: 'relative' }}>
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  borderRadius: 1
                }}
              >
                <CircularProgress />
              </Box>
            )}
            
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <DatePicker
                  label="시작 날짜"
                  value={dateRange[0]}
                  onChange={(newValue) => setDateRange([newValue, dateRange[1]])}
                  disabled={isLoading}
                  maxDate={dayjs()}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <DatePicker
                  label="종료 날짜"
                  value={dateRange[1]}
                  onChange={(newValue) => setDateRange([dateRange[0], newValue])}
                  disabled={isLoading}
                  maxDate={dayjs()}
                  minDate={dateRange[0]}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <Button
                  onClick={handleFetch}
                  disabled={!isValidRange || isLoading}
                  startIcon={isLoading ? <CircularProgress size={18} /> : <Refresh />}
                  variant="contained"
                  color="primary"
                  sx={{ height: 56, minWidth: 100 }}
                >
                  불러오기
                </Button>
                
                {isValidRange && (
                  <Tooltip title={t('dateRangeSelector.clearTooltip')}>
                    <IconButton
                      onClick={handleClear}
                      disabled={isLoading}
                      color="default"
                      sx={{ height: 56, width: 56 }}
                    >
                      <Clear />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </LocalizationProvider>

            {isLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                {t('dateRangeSelector.loadingMessage')}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
};

export default DateRangeSelector;